import LoanGraphData from '../models/LoanGraphData';

export const strategies = {
	MINIMUMS: 'MINIMUMS',
	AVALANCHE: 'AVALANCHE',
	SNOWBALL: 'SNOWBALL',
};

export const types = {
	SAVINGS: 'SAVINGS',
	LOANS: 'LOANS',
};

/**
 * Runs the loans forward a month at a time until every one is paid off, and returns a graph of each
 * one's balance with the totals the payoff page shows.
 *
 * Every loan pays its payment each month (the minimum, or what it has been paid lately when
 * `assumeRecentPayments` is set; see LoanGraphData::paymentFor). Avalanche and snowball then fix a
 * monthly budget in the selected month: `monthlyPayment`, or what the open loans are paying then if
 * that is more. Whatever of the budget the payments leave over goes to the loan with the highest rate
 * (avalanche) or the smallest balance (snowball), so a loan that is paid off rolls its payment on.
 *
 * @param {Object} options
 * @param {Array} options.categories loans: categories and ad hoc loans, anything LoanGraphData takes
 * @param {moment} options.selectedDate the month the strategies start from and the totals count from
 * @returns {Object|null} null when no loan has a balance to start from
 */
export default function projectPayoff({
	categories,
	type = types.LOANS,
	strategy = strategies.MINIMUMS,
	monthlyPayment = 0,
	selectedDate,
	yearsToExplore = 5,
	assumeRecentPayments = false,
	disabledGraphs = {},
}) {
	const datasets = [];
	const graphs = [];
	const allMonthsUsed = new Map();
	const incompleteGraphs = new Set();
	const activeGraphs = new Set();
	const graphsBeingCalculated = new Set();
	const totalInvested = new Map();
	let earliestStartDate = null;
	let totalMonthsIterated = 0;
	let totalInterestPaid = 0;
	let totalPaid = 0;
	let monthlyBudget = null;
	const extraBudget = parseFloat(monthlyPayment) || 0;

	for(let category of categories) {
		if(category.disabled) {
			continue;
		}
		const graph = new LoanGraphData(category);
		graph.type = type;
		graph.assumeRecentPayments = assumeRecentPayments;
		if(!graph.startDate) {
			continue;
		}
		incompleteGraphs.add(graph);
		if(!earliestStartDate || earliestStartDate.isAfter(graph.startDate)) {
			earliestStartDate = graph.startDate;
		}
		graph.currentDataSet.hidden = !!disabledGraphs[datasets.length];
		datasets.push(graph.currentDataSet);
		graphs.push(graph);
		totalInvested.set(graph, graph.amountRemaining);
	}

	if(!earliestStartDate) {
		return null;
	}

	let currentDate = earliestStartDate.clone();
	while(incompleteGraphs.size > 0) {
		for(let graph of incompleteGraphs) {
			if(graph.hasStartedAt(currentDate)) {
				activeGraphs.add(graph);
			}
		}
		let paidThisMonth = 0;
		for(let graph of activeGraphs) {
			graph.calculateCurrentMonth(currentDate);
			if(!graph.beginsAt(currentDate) && !graph.isFinished()) {
				graphsBeingCalculated.add(graph);
				totalInterestPaid += graph.addInterest(currentDate);
				const minimumPaid = graph.subtractMinimum(currentDate);
				totalPaid += minimumPaid;
				paidThisMonth += minimumPaid;
				const investmentChange = graph.matchBudgetIfNecessary(currentDate);
				if(investmentChange) {
					totalInvested.set(graph, investmentChange);
				}
			}
			if(graph.isFinished()) {
				incompleteGraphs.delete(graph);
				activeGraphs.delete(graph);
			}
			if(!graph.currentDataSet.hidden) {
				allMonthsUsed.set(currentDate, currentDate);
			}
		}

		if(strategy !== strategies.MINIMUMS && currentDate.isSameOrAfter(selectedDate, 'month')) {
			if(monthlyBudget === null) {
				monthlyBudget = Math.max(extraBudget, paidThisMonth);
			}
			let moneyToUse = monthlyBudget - paidThisMonth;
			let loanToApplyTo;
			do {
				loanToApplyTo = null;
				for(let graph of activeGraphs) {
					if(!graphsBeingCalculated.has(graph)) {
						continue;
					}
					if(!loanToApplyTo) {
						loanToApplyTo = graph;
					} else if(strategy === strategies.AVALANCHE && graph.getLastKnownInterestAmount() > loanToApplyTo.getLastKnownInterestAmount()) {
						loanToApplyTo = graph;
					} else if(strategy === strategies.SNOWBALL && graph.amountRemaining < loanToApplyTo.amountRemaining) {
						loanToApplyTo = graph;
					}
				}
				if(loanToApplyTo && moneyToUse > 0) {
					const before = moneyToUse;
					moneyToUse = loanToApplyTo.addPayment(currentDate, moneyToUse);
					totalPaid += before - moneyToUse;
					if(loanToApplyTo.isFinished()) {
						incompleteGraphs.delete(loanToApplyTo);
						activeGraphs.delete(loanToApplyTo);
					}
				}
			} while(moneyToUse > 0 && loanToApplyTo);
		}

		if((type === types.SAVINGS && totalMonthsIterated > (12 * yearsToExplore)) || (type !== types.SAVINGS && totalMonthsIterated > 1000)) {
			console.error('safety break!');
			break;
		}
		totalMonthsIterated++;
		currentDate = currentDate.clone().add(1, 'months');
	}

	const sortedMonths = [...allMonthsUsed.keys()].sort((a, b) => (a.isAfter(b) ? 1 : -1));
	for(let graph of graphs) {
		const dataset = graph.currentDataSet;
		dataset.data = sortedMonths.map((month) => (dataset.data.has(month) ? dataset.data.get(month) : null));
		dataset.payments = sortedMonths.map((month) => (dataset.payments.has(month) ? dataset.payments.get(month) : null));
		dataset.paid = sortedMonths.map((month) => graph.paidIn(month));
	}

	const allLoans = Array.from(graphsBeingCalculated);
	// What is still owed from the selected month on: the loans open then, and the money they take.
	const openLoans = allLoans.filter((loan) => loan.lastPaymentDate && !loan.lastPaymentDate.isBefore(selectedDate, 'month'));
	const debtFreeDate = openLoans.reduce((latest, loan) => (!latest || loan.lastPaymentDate.isAfter(latest) ? loan.lastPaymentDate : latest), null);

	return {
		data: {
			labels: sortedMonths.map((month) => month.format('MMM YYYY')),
			datasets,
		},
		allLoans,
		openLoans,
		debtFreeDate,
		paidOff: openLoans.every((loan) => loan.amountRemaining <= 0),
		warnings: [...new Set(allLoans.flatMap((loan) => [...loan.warnings]))],
		totalPaid,
		totalInterestPaid,
		totalInvested: [...totalInvested.values()].reduce((sum, amount) => sum + amount, 0),
		estimatedMinimum: openLoans.reduce((sum, loan) => sum + (Number(loan.lastKnownMinimumPayment) || 0), 0),
		currentPayments: openLoans.reduce((sum, loan) => sum + loan.ongoingPayment(), 0),
		// What the open loans were last actually paid, escrow and fees included; null when none has a transaction.
		lastPaid: openLoans.reduce((sum, loan) => {
			const paid = loan.lastPaid(selectedDate);
			return paid ? (sum || 0) + paid.amount : sum;
		}, null),
		monthlyBudget,
		paidFromSelected: openLoans.reduce((sum, loan) => sum + LoanGraphData.sumFrom(loan.paymentsMade, selectedDate), 0),
		interestFromSelected: openLoans.reduce((sum, loan) => sum + LoanGraphData.sumFrom(loan.interestsAdded, selectedDate), 0),
	};
}
